<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import AppErrorBoundary from '@/components/ui/AppErrorBoundary.vue'
import CelebrationOverlay from '@/components/gamification/CelebrationOverlay.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import OfflineBanner from '@/components/ui/OfflineBanner.vue'
import ToastHost from '@/components/ui/ToastHost.vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import LandingLayout from '@/layouts/LandingLayout.vue'
import LearnerLayout from '@/layouts/LearnerLayout.vue'
import PublicLayout from '@/layouts/PublicLayout.vue'

const route = useRoute()

const AUTH_ROUTES = new Set(['login', 'register', 'forgot-password', 'reset-password'])
const PUBLIC_ROUTES = new Set([
  'verify-certificate',
  'not-found',
  'forbidden',
  'public-course',
  'public-journey',
])

/**
 * The player gets its own chrome-free shell because it is the screen learners
 * spend the most time in; admin and learner areas get different navigation.
 */
const layout = computed(() => {
  const name = String(route.name ?? '')

  if (name === 'landing') return LandingLayout
  if (AUTH_ROUTES.has(name)) return AuthLayout
  if (PUBLIC_ROUTES.has(name) || name.startsWith('public-')) return PublicLayout
  if (name === 'course-player') return PublicLayout
  if (name === 'design' || name.startsWith('admin-')) return AdminLayout
  return LearnerLayout
})
</script>

<template>
  <OfflineBanner />
  <AppErrorBoundary>
    <component :is="layout">
      <RouterView v-slot="{ Component, route: current }">
        <template v-if="Component">
          <!-- Keyed by path so navigating between two lessons remounts the page
               and its data fetch, instead of silently reusing stale state. -->
          <component :is="Component" :key="current.path" />
        </template>
      </RouterView>
    </component>
  </AppErrorBoundary>

  <ToastHost />
  <ConfirmDialog />
  <CelebrationOverlay />
</template>
